import * as CdsIcons from '@central-design-system/icons';
import { computed, ref, watch } from 'vue';

import { deepEqual, pick, propsFactory, wrapInArray } from '@components/utils';

import { useProxyModel } from './proxyModel';

import type { CdsList } from '../components/CdsList';
import type { IInternalItem, IUseItems } from './items';
import type { TCdsIconsName } from '@central-design-system/icons';
import type { ComponentProps } from '@components/utils';
import type { PropType, ComputedRef, Ref } from 'vue';
import type { MaybePick } from '@components/utils';

export interface ISelectProps {
  multiple: boolean;
  openOnClear: boolean;
  hideSelected: boolean;
  hideNoData: boolean;
  isSelectAll: boolean;
  modelValue: any;
}

export const makeSelectProps = propsFactory(
  {
    /**
     * Множественный выбор элементов
     */
    multiple: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Открывать при очистке поля ввода
     */
    openOnClear: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Скрывать выбранные элементы из списка
     */
    hideSelected: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Скрывать отображаемый текст, если нет данных
     */
    hideNoData: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Добавляет возможность выбора всех элементов. Только для мультивыбора (multiple)
     */
    isSelectAll: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Отображать элементы выбранного списка в одну строку
     */
    ellipsis: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Свойства для списка
     */
    listProps: {
      type: Object as PropType<ComponentProps<typeof CdsList>>,
      default: undefined
    },
    /**
     * Иконка выпадающего меню
     */
    menuIcon: {
      type: String as PropType<TCdsIconsName>,
      default: 'chevron-down',
      validator: (value: TCdsIconsName) => CdsIcons.allowedIcons.includes(value)
    }
  },
  'select'
);

export function useSelect(
  props: ISelectProps,
  items: ComputedRef<IInternalItem[]> | Ref<IInternalItem[]>,
  transformIn: IUseItems['transformIn'],
  transformOut: IUseItems['transformOut']
) {
  const model = useProxyModel(
    props,
    'modelValue',
    [],
    (value) => transformIn(wrapInArray(value)),
    (value) => {
      const transformed = transformOut(value);
      return props.multiple ? transformed : transformed[0] ?? null;
    }
  );

  const selections = computed(() =>
    model.value.map((value) => items.value.find((item) => deepEqual(item.value, value.value)) || value)
  );
  const selected = computed(() => selections.value.map((selection) => selection.props.value));
  const selectedAll = computed(() => items.value.length === selections.value.length);
  const selectedIndeterminate = computed(() => selections.value.length > 0 && !selectedAll.value);
  const selectStrategy = computed(() => (props.multiple ? 'independent' : 'single-independent'));

  const displayItems = computed(() => {
    if (props.hideSelected) {
      return items.value.filter((item) => !selections.value.some((selection) => selection === item));
    }
    return items.value;
  });

  return { model, selections, selected, selectedAll, selectedIndeterminate, selectStrategy, displayItems };
}

export function useFilteredSelectProps<Props extends Record<string, unknown>>(
  props: Props,
  filteredProps: Partial<Props>,
  excludes: Extract<keyof MaybePick<Props, Extract<keyof Props, string>>, string>[]
) {
  const inputProps = ref<Partial<Props>>({});

  watch(
    () => props,
    () => {
      const [textInputProps] = pick(props, Object.keys(filteredProps) as any);
      const [filteredInputProps] = pick(textInputProps, excludes).reverse();

      inputProps.value = filteredInputProps;
    },
    { deep: true, immediate: true }
  );

  return { inputProps };
}
